<template>
  <section class="full-height">
    <div class="title" v-if="!fromTreeSection">
      <h3>Configura el horario</h3>
      <p>Establece el horario de disponibilidad del chatbot.</p>
    </div>

    <a-form ref="formRef" class="full-height" :model="formState" :rules="rules" layout="vertical">
      <a-form-item label="Seleccione los días de ejecución" name="daysSelecteds">
        <GroupSelectedButtons
          ref="groupSelectedButtons"
          :dataButtons="days"
          :allSelecteds="false"
          :disabled="disabled"
          @buttonSelecteds="setDaysSelected"
          :selecteds="formState.daysSelecteds"
        />
      </a-form-item>

      <CustomScrollbar :height="fromTreeSection ? 'calc(100% - 170px)' : '250px'">
        <a-form-item
          v-if="formState.arrayRangePickers.length > 0"
          label="Seleccione los rangos horarios por día"
          name="arrayRangePickers"
          style="width: 100%"
        >
          <list-range-pickers
            ref="arrayRangePickers"
            :fromTreeSection="fromTreeSection"
            :data-source="sortDataSource(formState.arrayRangePickers).slice()"
            :disabled="disabled"
            @onChangeRange="handleChangeRange"
            @onAddRange="handleAddRange"
            @onRemoveRange="handleRemoveRange"
          />
        </a-form-item>
      </CustomScrollbar>
    </a-form>
  </section>
</template>

<script>
import { defineComponent, ref, reactive, toRaw, inject, watch } from "vue";
import { transformIndexDays } from "@/app/shared/utils/transform";
import GroupSelectedButtons from "@/app/chatbots/components/buttons/GroupSelectedButtons.vue";
import ListRangePickers from "@/app/chatbots/components/organisms/ListRangePickers.vue";
import { groupScheduleConfig } from "@/app/chatbots/utils/groupScheduleConfig";
import { message } from "ant-design-vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

export default defineComponent({
  components: {
    GroupSelectedButtons,
    ListRangePickers,
    CustomScrollbar,
  },
  props: {
    fromTreeSection: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(_, { expose, emit }) {
    const chatbotDetails = inject("chatbotDetails");

    const formRef = ref();
    const formState = reactive({
      daysSelecteds: chatbotDetails?.value?.schedules
        ? groupScheduleConfig(
            chatbotDetails?.value?.schedules,
            (schedule) => schedule?.weekday
          ).map((schedule) => schedule?.day)
        : [],

      arrayRangePickers: chatbotDetails?.value?.schedules
        ? groupScheduleConfig(chatbotDetails?.value?.schedules, (schedule) => schedule?.weekday)
        : [],
    });
    const days = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];

    const rules = {
      daysSelecteds: [{ required: false, message: "Seleccione los días de ejecución." }],
      arrayRangePickers: [{ required: true, message: "Campo requerido" }],
    };

    /**
     * Valida que si hay dias seleccionados, los rangos esten completos
     */
    const validateRangePickers = () => {
      if (!formState.daysSelecteds.length) return true;
      const isValid = formState.arrayRangePickers.every((picker) => {
        return picker.hours.every((hour) => hour.start && hour.end);
      });

      return isValid;
    };

    const handleOk = (current) => {
      const isValid = validateRangePickers();
      if (!isValid) {
        message.error("Completa los campos");
        return current;
      }
      return current + 1;
    };

    expose({ formState: toRaw(formState), handleOk });

    watch(formState, () => emit("activate-button"), { deep: false });

    return {
      formRef,
      formState,
      days,
      rules,
    };
  },
  methods: {
    setDaysSelected(newDaysSelected) {
      const noDaysSelected = this.formState.daysSelecteds.filter(
        (daySelected) => !newDaysSelected.includes(daySelected)
      );
      noDaysSelected.forEach((day) => {
        this.removeDayToList(day);
      });

      this.formState.daysSelecteds = newDaysSelected;
      this.formState.daysSelecteds.forEach((day) => {
        const data = [...this.formState.arrayRangePickers].find((item) => item.order === day);
        if (data === undefined) {
          this.addDayToList(day);
        }
      });
    },
    addDayToList(day) {
      const itemList = {
        order: day,
        day: day,
        nameDay: this.nameDay(day),
        hours: [
          {
            id: `range-${day}-1`,
            start: null,
            end: null,
          },
        ],
      };
      this.formState.arrayRangePickers.push(itemList);
    },
    removeDayToList(day) {
      this.formState.arrayRangePickers = this.formState.arrayRangePickers.filter(
        (item) => item.order !== day
      );
    },
    nameDay(index) {
      const days = transformIndexDays([index]);
      return days[0]?.day;
    },
    sortDataSource(dataSource) {
      const data = [...dataSource];
      return data.sort((a, b) => a.order - b.order);
    },
    handleChangeRange(order, idRange, newRange) {
      const data = this.formState.arrayRangePickers.find((item) => item.order === order);
      const range = data.hours.find((hour) => hour.id === idRange);
      range.start = newRange.start;
      range.end = newRange.end;
      this.$emit("activate-button");
    },
    handleAddRange(order) {
      const day = this.formState.arrayRangePickers.find((item) => item.order === order);
      const newRange = {
        id: `range-${day.day}-${day.hours.length + 1}`,
        start: null,
        end: null,
      };
      if (day !== undefined) {
        day.hours.push(newRange);
      }
    },
    handleRemoveRange(order, keyRange) {
      const dayObj = this.formState.arrayRangePickers.find((item) => item.order === order);
      dayObj.hours = dayObj.hours.filter((hour) => hour.id !== keyRange);
      this.$emit("activate-button");
    },
    handleValidateListRanges() {
      this.$refs.arrayRangePickers?.handleSubmitRanges();
    },
    resetFields() {
      this.formRef.resetFields();
    },
  },
});
</script>

<style lang="sass" scoped>
.full-height
  height: 100%

.container-days
  box-sizing: border-box
  height: 52px
  left: 0px
  top: 24px
  border: 1px solid #D9D9D9
  border-radius: 8px

.icon-required
  margin-right: 4px
  color: #ff4d4f
  font-size: 14px
  font-family: SimSun, sans-serif
  line-height: 1

.title
  text-align: center

  h3
    color: $gray-9
    margin-bottom: 12px
    font-size: 24px
    font-weight: 600

  p
    color: $gray-8
    font-size: 16px
    margin-bottom: 24px
</style>
